import { Injectable, OnDestroy } from '@angular/core';
import type { User } from '@newgenus/common';
import { Observable, Subscription, distinctUntilChanged, map } from 'rxjs';
import { PresenceService } from './presence.service';

interface PresenceTarget {
    orgKey: string;
    userId: string;
    displayName: string;
}

/** Who should be marked online, and in which org — null when nobody is signed in with an org selected. */
function presenceTargetFor(users: User[] | null | undefined): PresenceTarget | null {
    const user = users?.[0];
    const orgKey = user?.organizations?.selected;
    if (!user?.uid || !orgKey) return null;
    return { orgKey, userId: user.uid, displayName: user.displayName || '' };
}

function sameTarget(a: PresenceTarget | null, b: PresenceTarget | null): boolean {
    return a?.orgKey === b?.orgKey && a?.userId === b?.userId && a?.displayName === b?.displayName;
}

/**
 * PresenceTrackerService — keeps the signed-in user online for the whole session.
 *
 * Presence used to be connected only by the org dashboard and the org board,
 * each of which disconnected on leave, so anyone working in their Workspace
 * showed offline. This root service is started once by AppComponent: it connects
 * for the user's selected org, moves them when they switch org, and disconnects
 * when they sign out. Pages only READ `PresenceService.onlineUsers$`.
 */
@Injectable({ providedIn: 'root' })
export class PresenceTrackerService implements OnDestroy {
    private subscription: Subscription | null = null;

    constructor(private presence: PresenceService) {}

    public start(users$: Observable<User[]>): void {
        if (this.subscription) return;

        let connected = false;
        this.subscription = users$
            .pipe(map(presenceTargetFor), distinctUntilChanged(sameTarget))
            .subscribe((target) => {
                if (target) {
                    this.presence.connect(target.orgKey, target.userId, target.displayName);
                    connected = true;
                } else if (connected) {
                    this.presence.disconnect();
                    connected = false;
                }
            });
    }

    public stop(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
    }

    ngOnDestroy(): void {
        this.stop();
    }
}
